import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useAuth } from '@/features/auth/AuthProvider';
import { fetchProfile, fetchProfileImageUrl } from './api';
import { displayName, type Profile } from './model';

export const profileKeys = {
  detail: (userId: string) => ['profile', userId] as const,
  image: (fileId: string) => ['profile-image', fileId] as const,
};

/**
 * The signed-in user's profile row.
 *
 * A missing profile is a successful result of `null`, not an error: plenty of
 * accounts will not have filled one in, and the UI falls back to the Appwrite
 * account name.
 */
export function useProfile() {
  const { userId, status, user } = useAuth();

  const query = useQuery<Profile | null>({
    queryKey: profileKeys.detail(userId ?? 'anonymous'),
    queryFn: () => fetchProfile(userId as string),
    enabled: status === 'authenticated' && userId !== null,
    // The ela app writes this rarely; there is no value in refetching often.
    staleTime: 5 * 60_000,
  });

  return {
    ...query,
    profile: query.data ?? null,
    name: displayName(query.data, user?.name, user?.email),
  };
}

/**
 * Loads a profile image and hands back an object URL.
 *
 * The URL is revoked when the file changes or the component unmounts —
 * without that, every navigation would leak the decoded image.
 */
export function useProfileImage(fileId: string | null | undefined) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (fileId == null) {
      setUrl(null);
      setFailed(false);
      return;
    }

    let cancelled = false;
    let created: string | null = null;
    setFailed(false);

    fetchProfileImageUrl(fileId)
      .then((objectUrl) => {
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        created = objectUrl;
        setUrl(objectUrl);
      })
      .catch((error: unknown) => {
        // A missing or unreadable image is a cosmetic problem: fall back to
        // initials rather than surfacing an error.
        console.warn('Could not load the profile image:', error);
        if (!cancelled) {
          setUrl(null);
          setFailed(true);
        }
      });

    return () => {
      cancelled = true;
      if (created !== null) URL.revokeObjectURL(created);
    };
  }, [fileId]);

  return { url, failed, loading: fileId != null && url === null && !failed };
}
